import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PicturesSlider from './PicturesSlider';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { ORDER_METHODS } from '../../services/exports/Constants';
import { collect } from 'collect.js';
import useMenu from '../../hooks/menu/useMenu';
import OrderMethodsTabBar from '../navigation/OrderMethodsTabBar';
import { StoreInterface } from '../../store/types';

export default function CompanyInfo() {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Company:CompanyInfo' });

  const { categories, generateMenuScrollQuery } = useMenu();

  const { franchise, company } = useSelector((state: StoreInterface) => state.initialData);
  const { cached_order } = useSelector((state: StoreInterface) => state.order);

  const renderImageCarousel = useMemo<JSX.Element>(() => {
    const pictureLength: number = company?.pictures?.length ?? 0;

    return (
      <div className="absolute inset-0 lg:rounded-b-xl rounded-b-[30px] overflow-hidden">
        {pictureLength > 0 && (
          <PicturesSlider pictures={company?.pictures} className={classNames('overflow-hidden h-full w-full')} />
        )}

        <div className="absolute inset-0 bg-black/20" />
      </div>
    );
  }, [company?.pictures, franchise?.has_multiple_companies]);

  const getPreparationTime = useMemo<string>(() => {
    const getTime = (min: number, max?: number) => {
      if (max) {
        return `${min} - ${max}`;
      }
      return min;
    };
    if (cached_order?.method === ORDER_METHODS.DELIVERY) {
      return t('preparation_time', {
        time: getTime(company?.average_order_delivery_time.min, company?.average_order_delivery_time.max),
      });
    }
    return t('preparation_time', {
      time: getTime(company?.average_order_preparation_time.min, company?.average_order_preparation_time.max),
    });
  }, [company?.average_order_preparation_time, company?.average_order_delivery_time, cached_order?.method, t]);

  return (
    <div className="h-fit w-full">
      <div className="w-full relative flex flex-col justify-center items-center">
        {renderImageCarousel}
        <div
          className={
            'relative lg:max-h-[60vh] inset-0 flex flex-col justify-center items-center lg:py-[145px] pb-[110px] pt-xl lg:px-none px-sm lg:max-w-[60%] mx-auto'
          }
        >
          <div className="lg:pb-[40px] pb-none flex flex-col justify-center items-center space-y-lg flex-wrap">
            <h1 className=" text-white text-center !font-parkinsans hyphens-auto" lang={company?.legal_lang}>
              {company?.name}
            </h1>
            <Link
              to={generateMenuScrollQuery(collect(categories).first()?.id)}
              viewTransition
              className={'w-fit bg-brand-primary rounded-xl px-lg h-[48px] flex items-center justify-center'}
            >
              <h6 className="text-white uppercase text-md font-semibold text-center">{t('buttons.order_now')}</h6>
            </Link>
          </div>
        </div>
        <div
          className="absolute -bottom-[30px] mx-auto z-10 space-y-md w-full lg:w-fit px-md lg:px-xl"
          style={{ zIndex: 30 }}
        >
          <h6 className="text-white text-[16px] text-center">{getPreparationTime}</h6>
          <OrderMethodsTabBar orderMethod={cached_order?.method} />
        </div>
      </div>
    </div>
  );
}
